import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, UserCheck, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API } from '../../utils/apiConfig';

export const MAX_CUSTOM_FIELDS = 6;
export const CUSTOM_VALUE_MAX = 128;

const MODE_TONE = { mandatory: 'border-red-300 text-red-700', optional: 'border-amber-300 text-amber-700', hidden: 'text-slate-400' };

export function CustomFieldsEditor({ fields, onChange }) {
  const list = fields || [];
  const update = (i, patch) => onChange(list.map((f, x) => (x === i ? { ...f, ...patch } : f)));
  const add = () => onChange([...list, { label: '', mode: 'optional' }]);
  return (
    <div className="space-y-2" data-testid="custom-fields-editor">
      <div className="flex items-center justify-between">
        <Label>Custom Questions <span className="text-xs text-slate-400 font-normal">({list.length}/{MAX_CUSTOM_FIELDS} · text answer up to {CUSTOM_VALUE_MAX} characters)</span></Label>
        <Button type="button" size="sm" variant="outline" onClick={add} disabled={list.length >= MAX_CUSTOM_FIELDS} data-testid="add-custom-field">
          <Plus className="w-4 h-4 mr-1" /> Add Field
        </Button>
      </div>
      {list.length === 0 && <p className="text-xs text-slate-400">No custom questions. Add up to {MAX_CUSTOM_FIELDS}, e.g. “Club name” or “Name on jersey”.</p>}
      {list.map((f, i) => (
        <div key={f.id || i} className="flex items-center gap-2" data-testid={`custom-field-${i}`}>
          <Input value={f.label} maxLength={128} placeholder={`Question ${i + 1}`} onChange={(e) => update(i, { label: e.target.value })} className="h-9" data-testid={`custom-field-label-${i}`} />
          <Select value={f.mode || 'optional'} onValueChange={(v) => update(i, { mode: v })}>
            <SelectTrigger className={`h-9 w-[124px] text-xs ${MODE_TONE[f.mode || 'optional']}`} data-testid={`custom-field-mode-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="hidden">Hidden</SelectItem>
              <SelectItem value="optional">Optional</SelectItem>
              <SelectItem value="mandatory">Mandatory</SelectItem>
            </SelectContent>
          </Select>
          <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`custom-field-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function EventAdminsPicker({ value, onChange }) {
  const [users, setUsers] = useState([]);
  const [pick, setPick] = useState('');
  useEffect(() => {
    axios.get(`${API}/users/event-admins`).then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);
  const selected = value || [];
  const available = users.filter((u) => !selected.includes(u.email));
  const label = (email) => {
    const u = users.find((x) => x.email === email);
    return u ? `${u.name} (${email})${u.is_active ? '' : ' · inactive'}` : email;
  };
  return (
    <div className="space-y-2" data-testid="event-admins-picker">
      <Label className="flex items-center gap-1"><UserCheck className="w-4 h-4" /> Event Admins</Label>
      <p className="text-xs text-slate-400">Assigned users log in at /office and can manage only this event. Create Event Admin accounts in User Management.</p>
      <div className="flex flex-wrap gap-1.5">
        {selected.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-1" data-testid={`event-admin-chip-${e}`}>
            {label(e)}
            <button type="button" onClick={() => onChange(selected.filter((x) => x !== e))} data-testid={`event-admin-remove-${e}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {selected.length === 0 && <span className="text-xs text-slate-400">None assigned</span>}
      </div>
      <div className="flex gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger className="h-9" data-testid="event-admin-select"><SelectValue placeholder={available.length ? 'Select an Event Admin login' : 'No more Event Admin users'} /></SelectTrigger>
          <SelectContent>
            {available.map((u) => <SelectItem key={u.email} value={u.email}>{u.name} — {u.email}{u.is_active ? '' : ' (inactive)'}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" disabled={!pick} onClick={() => { onChange([...selected, pick]); setPick(''); }} data-testid="event-admin-add-btn">Assign</Button>
      </div>
    </div>
  );
}

export function CustomResponsesInputs({ fields, values, onChange, missing = [], testPrefix = 'cf' }) {
  const visible = (fields || []).filter((f) => f.mode !== 'hidden');
  if (!visible.length) return null;
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-custom-responses`}>
      {visible.map((f) => {
        const v = values?.[f.id] || '';
        const bad = missing.includes(f.id);
        return (
          <div key={f.id} className="space-y-1">
            <Label className="text-sm">{f.label}{f.mode === 'mandatory' && <span className="text-red-500 ml-1 text-xs">*Required</span>}</Label>
            <Input value={v} maxLength={CUSTOM_VALUE_MAX} onChange={(e) => onChange({ ...values, [f.id]: e.target.value })}
              className={bad ? 'border-red-400 ring-2 ring-red-300' : ''} data-testid={`${testPrefix}-input-${f.id}`} />
            <div className="text-[10px] text-slate-400 text-right">{v.length}/{CUSTOM_VALUE_MAX}</div>
          </div>
        );
      })}
    </div>
  );
}

export const missingCustom = (fields, values) => (fields || [])
  .filter((f) => f.mode === 'mandatory' && !(values?.[f.id] || '').trim()).map((f) => f.id);

export const responsesToMap = (responses) => Object.fromEntries((responses || []).map((r) => [r.field_id, r.value || '']));
